import { Badge, MultiSelect, ScrollArea, Stack, Tabs } from '@mantine/core';
import { IconBuildingCommunity, IconCategory, IconFileDescription, IconListDetails, IconMapPin, IconSparkles } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { LookupRow } from '../api/types';
import { LookupManager } from '../components/LookupManager';
import { useToast } from '../components/Toast';
import { nameOf, tr } from '../lib/i18n';
import { DIRECTION } from '../lib/labels';
import { CategoriesPage } from './CategoriesPage';
import { FeaturesPage } from './FeaturesPage';
import { LocationsPage } from './LocationsPage';

const TABS = [
  { id: 'property', label: tr('أنواع العقار', 'Property types'), icon: IconBuildingCommunity },
  { id: 'request', label: tr('أنواع الطلب', 'Request types'), icon: IconFileDescription },
  { id: 'categories', label: tr('فئات العقار', 'Property categories'), icon: IconCategory },
  { id: 'features', label: tr('مميزات العقار', 'Property features'), icon: IconSparkles },
  { id: 'feature-groups', label: tr('مجموعات المميزات', 'Feature groups'), icon: IconListDetails },
  { id: 'locations', label: tr('البلاد والمواقع', 'Countries & locations'), icon: IconMapPin },
] as const;

type TabId = (typeof TABS)[number]['id'];

const DIRECTION_TONE: Record<string, string> = { OFFER: 'teal', DEMAND: 'blue', SERVICE: 'gray' };

/** Automatic matching: which offer types (بيع، تأجير) a request type (شراء، إيجار …) is matched with. */
function MatchCodes({ row }: { row: LookupRow }) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const offers = useQuery({
    queryKey: ['request-types', 'offers'],
    queryFn: () => api<{ items: LookupRow[] }>('/request-types', { query: { direction: 'OFFER' } }),
    enabled: row.direction === 'DEMAND',
  });
  const save = useMutation({
    mutationFn: (matchCodes: string[]) => api(`/request-types/${row.id}`, { method: 'PATCH', body: { matchCodes } }),
    onSuccess: () => { toast(tr('تم حفظ المطابقة', 'Matching saved')); void qc.invalidateQueries({ queryKey: ['request-types'] }); },
    onError: toastError,
  });
  if (row.direction !== 'DEMAND') return null;
  return (
    <MultiSelect
      size="xs"
      w={220}
      value={row.match_codes ?? []}
      disabled={save.isPending}
      onChange={(v) => save.mutate(v)}
      placeholder={(row.match_codes ?? []).length ? undefined : tr('بدون مطابقة', 'No matching')}
      data={(offers.data?.items ?? []).map((t) => ({ value: t.code, label: nameOf(t) }))}
      aria-label={tr('يطابق العروض', 'Matched with offers')}
      onClick={(e) => e.stopPropagation()}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

/** Platform master data: property/request types, categories, features, locations. */
export function ReferenceDataPage() {
  const [search, setSearch] = useSearchParams();
  const tabParam = (search.get('tab') || 'property') as TabId;
  const tabId: TabId = TABS.some((t) => t.id === tabParam) ? tabParam : 'property';

  const cats = useQuery({
    queryKey: ['property-categories', 'all'],
    queryFn: () => api<{ items: LookupRow[] }>('/property-categories', { query: { includeInactive: true } }),
    enabled: tabId === 'property',
  });
  const catOptions = (cats.data?.items ?? []).map((c) => ({ value: c.id, label: nameOf(c) }));
  const catName = (id?: number) => {
    const c = cats.data?.items.find((x) => x.id === id);
    return c ? nameOf(c) : '—';
  };

  return (
    <Stack gap="md">
      <Tabs value={tabId} onChange={(v) => v && setSearch({ tab: v })} variant="pills" radius="md" keepMounted={false}>
        <ScrollArea type="never" offsetScrollbars={false}>
          <Tabs.List style={{ flexWrap: 'nowrap', gap: 4, padding: 4, background: '#fff', border: '1px solid var(--pf-line)', borderRadius: 12, width: 'max-content' }}>
            {TABS.map((t) => (
              <Tabs.Tab key={t.id} value={t.id} leftSection={<t.icon size={16} stroke={1.7} />} style={{ whiteSpace: 'nowrap' }}>{t.label}</Tabs.Tab>
            ))}
          </Tabs.List>
        </ScrollArea>
      </Tabs>

      {tabId === 'property' && (
        <LookupManager
          path="property-types"
          queryKey="property-types"
          title={tr('أنواع العقار', 'Property types')}
          description={tr('الأنواع التي يختار منها العميل في النموذج، مرتّبة داخل فئاتها', 'The types a client picks from in the form, grouped by category')}
          addLabel={tr('نوع عقار جديد', 'New property type')}
          extraFields={[{ key: 'categoryId', column: 'category_id', label: tr('الفئة', 'Category'), options: catOptions, required: true, numeric: true }]}
          filter={{ key: 'categoryId', column: 'category_id', label: tr('الفئة', 'Category'), options: catOptions }}
          extraColumns={[{ label: tr('الفئة', 'Category'), render: (r) => <Badge variant="default" radius="sm" fw={500}>{catName(r.category_id)}</Badge>, sortValue: (r) => catName(r.category_id) }]}
        />
      )}

      {tabId === 'request' && (
        <LookupManager
          path="request-types"
          queryKey="request-types"
          title={tr('أنواع الطلب', 'Request types')}
          description={tr('العروض والطلبات والخدمات — ومع أي عروض تُطابَق الطلبات تلقائياً', 'Offers, demands and services — and which offers each demand is matched with')}
          addLabel={tr('نوع طلب جديد', 'New request type')}
          extraFields={[{ key: 'direction', column: 'direction', label: tr('الاتجاه', 'Direction'), required: true, createOnly: true, options: Object.entries(DIRECTION).map(([value, label]) => ({ value, label })) }]}
          extraColumns={[
            {
              label: tr('الاتجاه', 'Direction'),
              sortValue: (r) => r.direction ?? '',
              render: (r) => (
                <Badge variant="light" color={DIRECTION_TONE[r.direction ?? ''] ?? 'gray'} radius="sm" fw={500} tt="none">
                  {DIRECTION[r.direction ?? '']}
                </Badge>
              ),
            },
            { label: tr('يطابق العروض', 'Matched with'), render: (r) => <MatchCodes row={r} /> },
            { label: '', width: 70, render: (r) => (r.is_system ? <Badge variant="outline" color="gray" radius="sm" size="xs">{tr('أساسي', 'System')}</Badge> : null) },
          ]}
        />
      )}

      {tabId === 'categories' && <CategoriesPage />}
      {tabId === 'features' && <FeaturesPage />}
      {tabId === 'feature-groups' && (
        <LookupManager
          path="feature-groups"
          queryKey="feature-groups"
          title={tr('مجموعات المميزات', 'Feature groups')}
          description={tr('العناوين التي تُجمَّع تحتها المميزات في خطوة المميزات', 'The headings features are grouped under in the features step')}
          addLabel={tr('مجموعة جديدة', 'New group')}
        />
      )}
      {tabId === 'locations' && <LocationsPage />}
    </Stack>
  );
}
